import { createContext, useContext, useId, type ReactNode } from "react";
import { useT } from "@/i18n";
import { uiMessages } from "@/i18n/messages/ui";
import type uiEn from "@/i18n/messages/ui/en";
import { cn } from "@/lib/cn";
import { Icons } from "./icons";
import { StatusDot, type StatusTone } from "./status-dot";

export interface PocketRowProps {
  testId: string;
  /** 16px grey concept icon, or a 28px avatar or logo. */
  leading?: ReactNode;
  /** 13px/1.4, 500, --color-primary, one line with ellipsis, or wrapping inside a `PocketRowWrapScope`. */
  title: ReactNode;
  /** 11px/1.3, 500, --color-muted, one line with ellipsis, or wrapping inside a `PocketRowWrapScope`. */
  secondLine?: ReactNode;
  /** Value text or small buttons at the row end. */
  trailing?: ReactNode;
  /** With text the dot sits right before it; without, the dot sits alone at the end. */
  status?: { tone: StatusTone; text?: ReactNode };
  /** Shown on hover and focus-within; the status hides meanwhile. */
  hoverActions?: ReactNode;
  /** Under the text column, e.g. Deny and Allow. */
  below?: ReactNode;
  /** Drill-in chevron at the end (12px, --color-subtle). */
  chevron?: boolean;
  /** Keyboard selection: --color-interactive-hover fill. */
  selected?: boolean;
  /** Disable row activation while its action is pending. */
  disabled?: boolean;
  /** Set: the row activates on click, Enter and Space and gets the hover fill. */
  onSelect?: () => void;
  className?: string;
}

const WrapText = createContext(false);

/**
 * Rows inside wrap their title and second line onto as many lines as they
 * need instead of cutting them short: settings, where a label or a hint
 * must be read whole. Lists elsewhere keep one line each; `wrap={false}`
 * keeps it so inside.
 */
export function PocketRowWrapScope({
  wrap = true,
  children,
}: {
  wrap?: boolean;
  children: ReactNode;
}) {
  return <WrapText.Provider value={wrap}>{children}</WrapText.Provider>;
}

const STATUS_LABEL_KEY = {
  question: "statusQuestion",
  error: "statusError",
  unread: "statusUnread",
  working: "statusWorking",
} as const satisfies Record<StatusTone, keyof typeof uiEn>;

/**
 * Plain text at the row end truncates and keeps its full text as a tooltip.
 * The tooltip needs the pointer, so the text takes it and hands a click on to
 * the row (keyboard activation stays on the row's button).
 */
function isPlainText(node: ReactNode): node is string | number {
  return typeof node === "string" || typeof node === "number";
}

function EndText({
  children,
  onSelect,
}: {
  children: ReactNode;
  onSelect?: () => void;
}) {
  return isPlainText(children) ? (
    <span
      className="pointer-events-auto min-w-0 truncate"
      title={String(children)}
      onClick={onSelect}
    >
      {children}
    </span>
  ) : (
    children
  );
}

const SHOW_WITH_ACTIONS =
  "sr-only group-hover/pocket-row:not-sr-only group-focus-within/pocket-row:not-sr-only group-has-[[data-state=open]]/pocket-row:not-sr-only";
const HIDE_WITH_ACTIONS =
  "group-hover/pocket-row:hidden group-focus-within/pocket-row:hidden group-has-[[data-state=open]]/pocket-row:hidden";

/**
 * PocketRow — the compact-surface list row (compact surface standard §4, §5,
 * §7): 44px minimum (46px with a second line), 16px radius, 8px sides.
 *
 * DOM rules callers and tests rely on:
 * - With `onSelect` the text column is `<button data-testid={testId}>`; its
 *   ::after covers the row, so the whole row activates on click, Enter and
 *   Space. Without `onSelect` the root carries `data-testid` and nothing is
 *   focusable.
 * - The root is `[data-pocket-row]` (`data-selected="true"` when selected).
 * - `trailing`, `hoverActions` and `below` are siblings of the button, above
 *   its ::after, never inside it. Hover actions stay in the tab order while
 *   hidden and show on hover, focus-within or while one holds an open menu.
 */
export function PocketRow({
  testId,
  leading,
  title,
  secondLine,
  trailing,
  status,
  hoverActions,
  below,
  chevron = false,
  selected = false,
  disabled = false,
  onSelect,
  className,
}: PocketRowProps) {
  const t = useT(uiMessages);
  const statusId = useId();
  const fit = useContext(WrapText) ? "break-words" : "truncate";
  const hasSecondLine =
    secondLine !== undefined && secondLine !== null && secondLine !== false;
  const text = (
    <>
      <span
        className={cn("block text-appkit-body font-medium text-primary", fit)}
      >
        {title}
      </span>
      {hasSecondLine ? (
        <span
          className={cn("block text-appkit-small font-medium text-muted", fit)}
        >
          {secondLine}
        </span>
      ) : null}
    </>
  );
  const hasEnd = Boolean(status || trailing || hoverActions || chevron);
  const selectFromText = onSelect && !disabled ? onSelect : undefined;
  // Text at the end yields to the title past 60% of the row; controls keep
  // their own width.
  const endIsText = isPlainText(trailing) || isPlainText(status?.text);
  return (
    <div
      data-pocket-row=""
      data-testid={onSelect ? undefined : testId}
      data-selected={selected ? "true" : undefined}
      className={cn(
        "group/pocket-row relative flex items-center gap-2.5 rounded-[16px] px-2 py-1.5",
        hasSecondLine ? "min-h-[46px]" : "min-h-11",
        onSelect &&
          !disabled &&
          "hover:bg-[var(--color-interactive-hover)] has-[:focus-visible]:bg-[var(--color-interactive-hover)]",
        selected && "bg-[var(--color-interactive-hover)]",
        className,
      )}
    >
      {status ? (
        <span id={statusId} className="sr-only">
          {t(STATUS_LABEL_KEY[status.tone])}
        </span>
      ) : null}
      {leading ? (
        <span className="flex shrink-0 items-center text-muted">{leading}</span>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        {onSelect ? (
          <button
            type="button"
            data-testid={testId}
            onClick={onSelect}
            disabled={disabled}
            aria-describedby={status ? statusId : undefined}
            className="min-w-0 text-left outline-none disabled:cursor-default disabled:opacity-50 after:absolute after:inset-0 after:rounded-[16px] focus-visible:after:ring-2 focus-visible:after:ring-accent"
          >
            {text}
          </button>
        ) : (
          <div className="min-w-0">{text}</div>
        )}
        {below ? (
          <div className="relative z-10 mt-2 flex flex-wrap items-center gap-1.5">
            {below}
          </div>
        ) : null}
      </div>
      {hasEnd ? (
        <div
          className={cn(
            "pointer-events-none relative z-10 flex items-center justify-end gap-1.5 text-appkit-caption text-muted",
            endIsText ? "min-w-0 max-w-[60%]" : "shrink-0",
          )}
        >
          {status ? (
            <span
              data-pocket-row-status=""
              className={cn(
                "flex min-w-0 items-center gap-1.5",
                hoverActions && HIDE_WITH_ACTIONS,
              )}
            >
              <StatusDot tone={status.tone} />
              {status.text ? (
                <EndText onSelect={selectFromText}>{status.text}</EndText>
              ) : null}
            </span>
          ) : null}
          {trailing ? (
            <div
              data-pocket-row-trailing=""
              className="flex min-w-0 items-center gap-1.5 [&_:is(button,a,input,select,textarea,[role=button],[role=link])]:pointer-events-auto"
            >
              <EndText onSelect={selectFromText}>{trailing}</EndText>
            </div>
          ) : null}
          {hoverActions ? (
            <div
              data-pocket-row-hover-actions=""
              className={cn(
                "pointer-events-auto flex shrink-0 items-center gap-1",
                SHOW_WITH_ACTIONS,
              )}
            >
              {hoverActions}
            </div>
          ) : null}
          {chevron ? (
            <Icons.ChevronRight
              className="h-3 w-3 shrink-0 text-subtle"
              aria-hidden="true"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
